import classNames from "classnames";
import {
  FreeTrialRole,
  InitState,
  InitUserRole,
  useUserInitAccess,
  useWebSocket,
  UUID,
} from "@gx1/gx-npm-lib";
import { BannerComponent, InitAccessRevokedDialogComponent, InitDeletedDialogComponent } from "@gx1/gx-ui-common";
import { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { MainNavAppV2Context } from "../../app-v2.context";
import { WebSocketMessageEvent } from "../../app.constants";
import ActiveViewerAvatarsComponent from "./active-viewer-avatars/active-viewer-avatars.component";
import BreadcrumbsComponent from "./breadcrumbs/breadcrumbs.component";
import InitStateBannerComponent from "./init-state-banner/init-state-banner.component";
import ManageTeamBtnComponent from "./manage-team-btn/manage-team-btn.component";
import SaveIndicatorComponent from "./save-indicator/save-indicator.component";
import ViewOnlyBadgeComponent from "./view-only-badge/view-only-badge.component";
import styles from "./eval-header.styles.module.scss";
import { navigateToUrl } from "single-spa";

enum WebSocketMessageAction {
  MESSAGE = "message"
}

type WebSocketPublishMessage = {
  action: WebSocketMessageAction;
  data: {
    event: WebSocketMessageEvent;
    initiativeId: UUID;
    isViewing: boolean;
  };
};

type WebSocketInitUpdateMessage = {
  event: WebSocketMessageEvent;
  initiativeId: UUID;
  name?: string;
  state?: InitState;
};

type WebSocketDialogMessage = {
  event: WebSocketMessageEvent.INITIATIVE_ACCESS_REVOKED | WebSocketMessageEvent.INITIATIVE_DELETED;
  initiativeId: UUID;
  name: string;
};

type EvaluationHeaderProps = {
  freeTrialRole?: string;
};

const EvaluationHeaderComponent = ({ freeTrialRole = "" }: EvaluationHeaderProps) => {
  const { t } = useTranslation();
  const { setInitState, setInitName, setStateUpdateDate } = useContext(MainNavAppV2Context);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const { initiativeId = "" } = useParams<{ initiativeId: UUID }>();
  const { hasLoadedAccess, role } = useUserInitAccess(initiativeId);
  const [wsMessage, wsPublish] = useWebSocket<WebSocketInitUpdateMessage, WebSocketPublishMessage>();
  const [wsInMsgRevokeAccess] = useWebSocket<WebSocketDialogMessage, WebSocketPublishMessage>();
  const [wsInMsgDeleted] = useWebSocket<WebSocketDialogMessage, WebSocketPublishMessage>();
  const [isAccessRevokedDialogOpen, setIsAccessRevokedDialogOpen] = useState(false);
  const [isDeletedDialogOpen, setIsDeletedDialogOpen] = useState(false);
  const [userFullName, setUserFullName] = useState("");
  const isFreeTrialUser = freeTrialRole === FreeTrialRole.FREE_TRIAL_GUEST || freeTrialRole === FreeTrialRole.FREE_TRIAL_RECIPIENT;

  const handleUserViewingStatusChange = useCallback((isViewing: boolean) => {
    if (!(initiativeId && hasLoadedAccess)) {
      return;
    }
    const event = WebSocketMessageEvent.VIEWING_INITIATIVE;
    const data = { event, initiativeId: initiativeId as UUID, isViewing };
    wsPublish({ action: WebSocketMessageAction.MESSAGE, data });
  }, [hasLoadedAccess, initiativeId, wsPublish]);

  useEffect(() => {
    handleUserViewingStatusChange(true);
    return () => {
      handleUserViewingStatusChange(false);
    };
  }, [handleUserViewingStatusChange]);

  useEffect(() => {
    if (!wsMessage || wsMessage.event !== WebSocketMessageEvent.CONNECT) {
      return;
    }
    handleUserViewingStatusChange(true);
  }, [handleUserViewingStatusChange, wsMessage]);

  useEffect(() => {
    if (!Boolean(initiativeId)) {
      setInitName("");
      setInitState("");
      setStateUpdateDate("");
      return;
    }
    void (async () => {
      setIsLoading(true);
      try {
        const url = `${window.gxApiConnection}/api/v2/initiatives/${initiativeId}/header`;
        const response = await window.fetch(url, { credentials: "include", method: "GET" });
        if (!response.ok) {
          throw new Error();
        }
        const { data } = await response.json();
        setInitName(data.name || "");
        setInitState(data.state || "");
        setStateUpdateDate(data.stateUpdateDate || "");
      } catch {
        setHasLoadError(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [initiativeId, setInitName, setInitState, setStateUpdateDate]);

  useEffect(() => {
    if (!initiativeId || !wsMessage || wsMessage.initiativeId !== initiativeId) {
      return;
    }

    if (wsMessage.event === WebSocketMessageEvent.INIT_NAME_UPDATED) {
      setInitName(wsMessage.name || "");
    } else if (wsMessage.event === WebSocketMessageEvent.UPDATE_INITIATIVE_STATE) {
      setInitState(wsMessage.state || "");
      setStateUpdateDate(new Date().toISOString());
    }
  }, [initiativeId, wsMessage, setInitName, setInitState, setStateUpdateDate]);

  useEffect(() => {
    if (!initiativeId || !wsInMsgRevokeAccess || wsInMsgRevokeAccess.initiativeId !== initiativeId) {
      return;
    }
    if (wsInMsgRevokeAccess.event !== WebSocketMessageEvent.INITIATIVE_ACCESS_REVOKED) {
      return;
    }
    setIsAccessRevokedDialogOpen(true);
    setUserFullName(wsInMsgRevokeAccess.name);
  }, [initiativeId, wsInMsgRevokeAccess, wsMessage]);

  const handleAccessRevokedDialogClick = () => {
    setIsAccessRevokedDialogOpen(false);
    navigateToUrl("/s/evaluations");
  };

  useEffect(() => {
    if (!initiativeId || !wsInMsgDeleted || wsInMsgDeleted.initiativeId !== initiativeId) {
      return;
    }
    if (wsInMsgDeleted.event !== WebSocketMessageEvent.INITIATIVE_DELETED) {
      return;
    }
    setIsDeletedDialogOpen(true);
    setUserFullName(wsInMsgDeleted.name);
  }, [initiativeId, wsInMsgDeleted]);

  const handleDeletedDialogClick = () => {
    setIsDeletedDialogOpen(false);
    navigateToUrl("/s/evaluations");
  };

  return (
    <div className={classNames(styles.root, isFreeTrialUser && styles.hasFreeTrialHeader)}>
      <div className={styles.header}>
        <div className={classNames(styles.headerWrapper, isLoading && styles.loading)}>
          {!isLoading && (
            <Fragment>
              <section>
                <BreadcrumbsComponent />
              </section>
              <section className={styles.rightSection}>
                <InitStateBannerComponent />
                {role === InitUserRole.VIEWER && <ViewOnlyBadgeComponent />}
                <ActiveViewerAvatarsComponent />
                {role !== InitUserRole.VIEWER && <ManageTeamBtnComponent />}
                {role !== InitUserRole.VIEWER && <SaveIndicatorComponent />}
              </section>
            </Fragment>
          )}
        </div>
        {hasLoadError &&
          <BannerComponent
            variant="error"
            onCloseClick={() => setHasLoadError(false)}
            message={t("Oops... something went wrong. Please try again.")}
          />
        }
      </div>
      <InitAccessRevokedDialogComponent
        userFullName={userFullName}
        isOpen={isAccessRevokedDialogOpen}
        handleClick={handleAccessRevokedDialogClick}
      />
      <InitDeletedDialogComponent
        userFullName={userFullName}
        isOpen={isDeletedDialogOpen}
        handleClick={handleDeletedDialogClick}
      />
    </div>
  );
};

export default EvaluationHeaderComponent;
